import {
  AlertCircle,
  AlertTriangle,
  Ban,
  CheckCircle2,
  CircleDashed,
  CircleDot,
  CirclePause,
  Clock,
  HelpCircle,
  Loader,
  type LucideIcon,
  MinusCircle,
  PlugZap,
  RotateCcw,
  Search,
  ShieldX,
  XCircle,
} from "lucide-react";
import type {
  AiRunStatus,
  AppointmentStatus,
  AuditResult,
  ConversationAiStatus,
  DeliveryStatus,
  ErrorSeverity,
  ErrorStatus,
  HealthStatus,
  IntegrationStatus,
  JobState,
  MessageAiStatus,
  OfferRunStatus,
  OfferRunStepStatus,
  OpportunityStatus,
  OrganizationStatus,
  PurchaseStatus,
  StaffStatus,
  StoreStatus,
  UserStatus,
} from "@/api/types";

/**
 * Centralized status vocabulary: label, description, icon and semantic tone. Components look
 * statuses up here — never hard-code colors for raw values. `statusOf` renders unknown values
 * from the backend neutrally instead of crashing.
 */
export type Tone = "neutral" | "success" | "warning" | "danger" | "info";
export interface StatusConfig {
  label: string;
  tone: Tone;
  icon: LucideIcon;
  description?: string;
}

export const TONE: Record<Tone, { text: string; bg: string; dot: string; ring: string }> = {
  neutral: { text: "text-muted-foreground", bg: "bg-muted", dot: "bg-muted-foreground/60", ring: "ring-border" },
  success: { text: "text-success", bg: "bg-success-muted", dot: "bg-success", ring: "ring-success/25" },
  warning: { text: "text-warning", bg: "bg-warning-muted", dot: "bg-warning", ring: "ring-warning/30" },
  danger: { text: "text-danger", bg: "bg-danger-muted", dot: "bg-danger", ring: "ring-danger/25" },
  info: { text: "text-info", bg: "bg-info-muted", dot: "bg-info", ring: "ring-info/25" },
};

export function statusOf<K extends string>(map: Record<K, StatusConfig>, value: string | null | undefined): StatusConfig {
  if (!value) return { label: "—", tone: "neutral", icon: MinusCircle };
  return (
    (map as Record<string, StatusConfig>)[value] ?? {
      label: value.replaceAll("_", " ").toLowerCase(),
      tone: "neutral",
      icon: HelpCircle,
      description: "Status not recognized by this console",
    }
  );
}

export const HEALTH: Record<HealthStatus, StatusConfig> = {
  OPERATIONAL: { label: "Operational", tone: "success", icon: CheckCircle2 },
  DEGRADED: { label: "Degraded", tone: "warning", icon: AlertTriangle },
  UNAVAILABLE: { label: "Unavailable", tone: "danger", icon: XCircle },
  UNKNOWN: { label: "Unknown", tone: "neutral", icon: HelpCircle, description: "No health signal available" },
};

export const ORGANIZATION: Record<OrganizationStatus, StatusConfig> = {
  ACTIVE: { label: "Active", tone: "success", icon: CheckCircle2 },
  SUSPENDED: { label: "Suspended", tone: "danger", icon: Ban, description: "Users can't sign in; Rogue stops messaging sellers" },
};

export const USER: Record<UserStatus, StatusConfig> = {
  ACTIVE: { label: "Active", tone: "success", icon: CheckCircle2 },
  SUSPENDED: { label: "Suspended", tone: "danger", icon: Ban },
  INVITED: { label: "Invited", tone: "info", icon: Clock, description: "Hasn't accepted the invitation" },
};

export const STORE: Record<StoreStatus, StatusConfig> = {
  ACTIVE: { label: "Active", tone: "success", icon: CheckCircle2 },
  INACTIVE: { label: "Inactive", tone: "neutral", icon: CirclePause, description: "Deactivated: hidden from dealer users; Rogue doesn't work it" },
  SUSPENDED: { label: "Suspended", tone: "danger", icon: Ban, description: "The organization is suspended" },
};

export const STAFF: Record<StaffStatus, StatusConfig> = {
  ACTIVE: { label: "Active", tone: "success", icon: CheckCircle2 },
  DISABLED: { label: "Disabled", tone: "danger", icon: Ban, description: "Can't sign in to the admin console" },
};

export const AI_RUN: Record<AiRunStatus, StatusConfig> = {
  QUEUED: { label: "Queued", tone: "info", icon: Clock, description: "Waiting in the conversation-ai queue" },
  PROCESSING: { label: "Processing", tone: "info", icon: Loader },
  COMPLETED: { label: "Completed", tone: "success", icon: CheckCircle2 },
  FAILED: { label: "Failed", tone: "danger", icon: XCircle, description: "No reply was sent for this run" },
  STALE: { label: "Stale", tone: "warning", icon: RotateCcw, description: "Superseded — newer seller messages arrived while it ran" },
  CANCELLED: { label: "Cancelled", tone: "neutral", icon: MinusCircle, description: "A human took over the conversation" },
};

export const CONVERSATION_AI: Record<ConversationAiStatus, StatusConfig> = {
  IDLE: { label: "Idle", tone: "neutral", icon: CircleDashed },
  QUEUED: { label: "Queued", tone: "info", icon: Clock },
  PROCESSING: { label: "Processing", tone: "info", icon: Loader },
  FAILED: { label: "Failed", tone: "danger", icon: XCircle },
};

export const MESSAGE_AI: Record<MessageAiStatus, StatusConfig> = {
  PENDING: { label: "AI pending", tone: "info", icon: Clock },
  PROCESSING: { label: "AI processing", tone: "info", icon: Loader },
  PROCESSED: { label: "AI processed", tone: "success", icon: CheckCircle2 },
  FAILED: { label: "AI failed", tone: "danger", icon: XCircle },
  SKIPPED: { label: "Not for AI", tone: "neutral", icon: MinusCircle },
};

export const DELIVERY: Record<DeliveryStatus, StatusConfig> = {
  NOT_SENT: { label: "Held (not sent)", tone: "warning", icon: CirclePause },
  QUEUED: { label: "Queued", tone: "neutral", icon: Clock },
  SENT: { label: "Sent", tone: "info", icon: CircleDot },
  DELIVERED: { label: "Delivered", tone: "success", icon: CheckCircle2 },
  READ: { label: "Read", tone: "success", icon: CheckCircle2 },
  FAILED: { label: "Delivery failed", tone: "danger", icon: XCircle },
};

export const JOB: Record<JobState, StatusConfig> = {
  waiting: { label: "Waiting", tone: "info", icon: Clock },
  active: { label: "Active", tone: "info", icon: Loader },
  completed: { label: "Completed", tone: "success", icon: CheckCircle2 },
  failed: { label: "Failed", tone: "danger", icon: XCircle },
  delayed: { label: "Delayed", tone: "warning", icon: Clock },
  paused: { label: "Paused", tone: "warning", icon: CirclePause },
};

export const OFFER_RUN: Record<OfferRunStatus, StatusConfig> = {
  SUCCEEDED: { label: "Succeeded", tone: "success", icon: CheckCircle2, description: "The API quoted this lead" },
  DECLINED: { label: "Declined", tone: "warning", icon: Ban, description: "The API won't quote this lead: staff set the max offer" },
  FAILED: { label: "Failed", tone: "danger", icon: XCircle, description: "An error: the call is retried" },
};

export const OFFER_RUN_STEP: Record<OfferRunStepStatus, StatusConfig> = {
  OK: { label: "OK", tone: "success", icon: CheckCircle2 },
  WARN: { label: "Warning", tone: "warning", icon: AlertTriangle },
  FAILED: { label: "Failed", tone: "danger", icon: XCircle },
  SKIPPED: { label: "Skipped", tone: "neutral", icon: MinusCircle },
};

export const INTEGRATION: Record<IntegrationStatus, StatusConfig> = {
  CONNECTED: { label: "Connected", tone: "success", icon: CheckCircle2 },
  DEGRADED: { label: "Degraded", tone: "warning", icon: AlertTriangle },
  FAILING: { label: "Failing", tone: "danger", icon: PlugZap },
  DISABLED: { label: "Disabled", tone: "neutral", icon: MinusCircle },
  NOT_CONFIGURED: { label: "Not configured", tone: "neutral", icon: CircleDashed },
  INACTIVE: { label: "Inactive", tone: "neutral", icon: MinusCircle },
  OFFER_API_OFF: { label: "Offer API off", tone: "warning", icon: AlertTriangle },
  NO_STORES: { label: "No stores", tone: "warning", icon: AlertTriangle },
  READY: { label: "Ready", tone: "info", icon: CheckCircle2 },
};

export const PURCHASE: Record<PurchaseStatus, StatusConfig> = {
  ACQUIRED: { label: "Acquired", tone: "info", icon: CircleDot },
  IN_RECON: { label: "In recon", tone: "warning", icon: Clock },
  FRONTLINE: { label: "Frontline", tone: "success", icon: CheckCircle2 },
  SOLD: { label: "Sold", tone: "success", icon: CheckCircle2 },
  WHOLESALED: { label: "Wholesaled", tone: "neutral", icon: MinusCircle },
};

export const AUDIT_RESULT: Record<AuditResult, StatusConfig> = {
  SUCCESS: { label: "Success", tone: "success", icon: CheckCircle2 },
  FAILURE: { label: "Failure", tone: "danger", icon: XCircle },
  DENIED: { label: "Denied", tone: "warning", icon: ShieldX },
};

export const ERROR_STATUS: Record<ErrorStatus, StatusConfig> = {
  OPEN: { label: "Open", tone: "danger", icon: AlertCircle },
  INVESTIGATING: { label: "Investigating", tone: "warning", icon: Search },
  RESOLVED: { label: "Resolved", tone: "success", icon: CheckCircle2 },
  IGNORED: { label: "Ignored", tone: "neutral", icon: MinusCircle },
};

export const SEVERITY: Record<ErrorSeverity, StatusConfig> = {
  CRITICAL: { label: "Critical", tone: "danger", icon: AlertCircle },
  ERROR: { label: "Error", tone: "danger", icon: XCircle },
  WARNING: { label: "Warning", tone: "warning", icon: AlertTriangle },
};

export const OPPORTUNITY_STATUS: Record<OpportunityStatus, StatusConfig> = {
  OPEN: { label: "Open", tone: "info", icon: CircleDot },
  DISQUALIFIED: { label: "Disqualified", tone: "neutral", icon: Ban },
  OPTED_OUT: { label: "Opted out", tone: "danger", icon: Ban },
  UNRESPONSIVE: { label: "Unresponsive", tone: "neutral", icon: Clock },
  LOST: { label: "Lost", tone: "danger", icon: XCircle },
  EXPIRED: { label: "Expired", tone: "neutral", icon: Clock },
  DUPLICATE: { label: "Duplicate", tone: "neutral", icon: MinusCircle },
};

export const APPOINTMENT: Record<AppointmentStatus, StatusConfig> = {
  PROPOSED: { label: "Proposed", tone: "warning", icon: Clock },
  SCHEDULED: { label: "Scheduled", tone: "info", icon: Clock },
  CONFIRMED: { label: "Confirmed", tone: "success", icon: CheckCircle2 },
  SHOWED: { label: "Showed", tone: "success", icon: CheckCircle2 },
  NO_SHOW: { label: "No-show", tone: "danger", icon: XCircle },
  CANCELLED: { label: "Cancelled", tone: "neutral", icon: MinusCircle },
};

export const SOURCE_LABEL: Record<string, string> = {
  KBB_ICO: "KBB ICO",
  CRM: "CRM",
  FACEBOOK: "Facebook",
};

export const ROLE_LABEL: Record<string, string> = {
  SUPER_ADMIN: "Super admin",
  ADMIN: "Admin",
  SUPPORT: "Support",
  OPERATIONS: "Operations",
  FINANCE: "Finance",
  READ_ONLY: "Read-only",
  OWNER: "Owner",
  GROUP_MANAGER: "Group manager",
  MEMBER: "Member",
  STORE_MANAGER: "Store manager",
  BUYER: "Buyer",
  BDC: "BDC",
  APPRAISER: "Appraiser",
  VIEWER: "Viewer",
};

export const STAGE_LABEL = (s: string) =>
  s
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
